import styles from './ChatActions.module.scss';
import IconButton from 'threads5/IconButton';
import Icon from 'threads5/Icon';
import { useChatAnalytics } from './ChatAnalytics';
import { CHAT_ANSWER_ELEMENT_ID } from './ChatAnswer';

// A module that provides action buttons for chat answers, including copying the answer,
// copying a link to the conversation, and providing feedback through thumbs up/down buttons

// Custom thumbs up/down SVG icons
const ThumbUpIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    {...props}
  >
    <path
      d='M7 22H4C3.46957 22 2.96086 21.7893 2.58579 21.4142C2.21071 21.0391 2 20.5304 2 20V13C2 12.4696 2.21071 11.9609 2.58579 11.5858C2.96086 11.2107 3.46957 11 4 11H7M14 9V5C14 4.20435 13.6839 3.44129 13.1213 2.87868C12.5587 2.31607 11.7956 2 11 2L7 11V22H18.28C18.7623 22.0055 19.2304 21.8364 19.5979 21.524C19.9654 21.2116 20.2077 20.7769 20.28 20.3L21.66 11.3C21.7035 11.0134 21.6842 10.7207 21.6033 10.4423C21.5225 10.1638 21.3821 9.90629 21.1919 9.68751C21.0016 9.46873 20.7661 9.29393 20.5016 9.17522C20.2371 9.0565 19.9499 8.99672 19.66 9H14Z'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const ThumbDownIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
    {...props}
  >
    <path
      d='M17 2H20C20.5304 2 21.0391 2.21071 21.4142 2.58579C21.7893 2.96086 22 3.46957 22 4V11C22 11.5304 21.7893 12.0391 21.4142 12.4142C21.0391 12.7893 20.5304 13 20 13H17M10 15V19C10 19.7956 10.3161 20.5587 10.8787 21.1213C11.4413 21.6839 12.2044 22 13 22L17 13V2H5.72C5.23772 1.99448 4.76964 2.16359 4.40209 2.47599C4.03454 2.78839 3.79232 3.22309 3.72 3.7L2.34 12.7C2.29647 12.9866 2.31583 13.2793 2.39666 13.5577C2.4775 13.8362 2.61788 14.0937 2.80812 14.3125C2.99837 14.5313 3.23386 14.7061 3.49839 14.8248C3.76291 14.9435 4.05009 15.0033 4.34 15H10Z'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

interface Props {
  feedbackHandler: (feedback: string) => void;
  selectedFeedback: string;
  markdownText?: string;
  questionId?: string;
  chatState: {
    lastQuestionID: React.MutableRefObject<string>;
    likelyInternal: React.MutableRefObject<boolean>;
    timeQuestionSubmitted: React.MutableRefObject<number>;
  };
}

const ChatActions = ({
  feedbackHandler,
  selectedFeedback,
  markdownText,
  questionId,
  chatState,
}: Props) => {
  const { trackCopyAnswer, trackCopyLink } = useChatAnalytics(chatState);
  const handleCopyAnswer = async () => {
    if (!markdownText) return;

    try {
      const plainText =
        document.getElementById(CHAT_ANSWER_ELEMENT_ID)?.innerText ??
        markdownText;
      await navigator.clipboard.writeText(plainText);

      // Track the copy answer event
      trackCopyAnswer();
    } catch (err) {
      console.error('Failed to copy answer:', err);
    }
  };

  const handleCopyLink = async () => {
    if (!questionId) return;

    try {
      // Generate a URL that includes the question ID
      const baseUrl = window.location.origin + window.location.pathname;
      const conversationUrl = `${baseUrl}?conversation=${questionId}`;

      await navigator.clipboard.writeText(conversationUrl);

      // Track the copy link event
      trackCopyLink();
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  return (
    <>
      <div className={styles.actionsContainer}>
        <div className={styles.actionsTitle}>ACTIONS</div>

        <div className={styles.actionsRow}>
          <div className={styles.buttonsContainer}>
            {markdownText && questionId && (
              <>
                <div className={styles.buttonRow}>
                  <div
                    className={styles.copyButton}
                    onClick={handleCopyAnswer}
                    role='button'
                    tabIndex={0}
                  >
                    <Icon name='Copy' size='20' />
                    <span className={styles.buttonText}>Copy answer</span>
                  </div>
                </div>
                <div className={styles.buttonRow}>
                  <div
                    className={styles.copyButton}
                    onClick={handleCopyLink}
                    role='button'
                    tabIndex={0}
                  >
                    <Icon name='Link' size='20' />
                    <span className={styles.buttonText}>Copy link</span>
                  </div>
                </div>
                <div className={styles.buttonRow}>
                  <div className={styles.buttonWrapper}>
                    <IconButton
                      appearance='solid'
                      size='32'
                      intent={
                        selectedFeedback === 'good' ? 'prominent' : 'neutral'
                      }
                      icon={(restProps) => {
                        return (
                          <ThumbUpIcon style={{ color: 'currentColor' }} />
                        );
                      }}
                      sx={{
                        color:
                          selectedFeedback === 'good' ? '#FFFFFF' : '#000033',
                      }}
                      onClick={() => feedbackHandler('good')}
                    />
                    <span className={styles.tooltip}>Helpful</span>
                  </div>
                  <div className={styles.buttonWrapper}>
                    <IconButton
                      appearance='solid'
                      size='32'
                      intent={
                        selectedFeedback === 'bad' ? 'negative' : 'neutral'
                      }
                      icon={(restProps) => {
                        return (
                          <ThumbDownIcon style={{ color: 'currentColor' }} />
                        );
                      }}
                      sx={{
                        color:
                          selectedFeedback === 'bad' ? '#FFFFFF' : '#000033',
                      }}
                      onClick={() => feedbackHandler('bad')}
                    />
                    <span className={styles.tooltip}>Not helpful</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

ChatActions.displayName = 'ChatActions';
export default ChatActions;
